import { ENV } from "@/config/env";
import { getPassportToken, saveStreamAuth } from "@/services/auth/tokenStore";
import { authEventEmitter } from "@/services/auth/authEvents";
import * as Device from 'expo-device';
import * as Notifications from 'expo-notifications';
import { Platform } from "react-native";

export async function fetchStreamToken() {
    const passportToken = await getPassportToken();
    
    if (!passportToken) {
        throw new Error("No passport token found");
    }

    try {
        const response = await fetch(`${ENV.API_BASE_URL}/api/stream/token`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${passportToken}`,
                'Accept': 'application/json',
            },
        });

        if (!response.ok) {
            if (response.status === 401) {
                authEventEmitter.emitLogout();
                return { streamUserId: null, streamToken: null };
            }
            throw new Error(`Failed to fetch stream token: ${response.statusText}`);
        }

        const data = await response.json();
        console.log("Stream token response:", data);
        
        // Extract streamUserId and streamToken from the actual API response structure
        const streamToken = data.stream_token;
        const streamUserId = data.stream_user?.id;

        if (streamUserId && streamToken) {
            console.log("Stream token fetched from universe:", streamToken);
            await saveStreamAuth({ userId: streamUserId, token: streamToken });
        }

        return { streamUserId, streamToken };
    } catch (error) {
        console.error("Error fetching stream token from universe:", error);
        throw error;
    }
}

export async function registerDeviceToken(token: string, status: 'active' | 'inactive' = 'active', name: string = Device.deviceName ?? 'Mobile App',) {
    const passportToken = await getPassportToken();

    if (!passportToken) {
        throw new Error("No passport token found");
    }

    try {
        const response = await fetch(`${ENV.API_BASE_URL}/api/stream/register-device`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${passportToken}`,
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                token,
                name,
                status,
            }),
        });
        console.log("Device registration response:", response);
        if (!response.ok) {
            if (response.status === 401) {
                // If this is an unregistration (status === 'inactive'), we don't want to emit logout
                // to avoid potential loops, though logout itself calls this.
                if (status === 'active') {
                    authEventEmitter.emitLogout();
                }
                return null;
            }
            const errorData = await response.json().catch(() => ({}));
            throw new Error(`Failed to register device: ${response.statusText} ${JSON.stringify(errorData)}`);
        }

        const data = await response.json();
        console.log("Device registration response:", data);
        return data;
    } catch (error) {
        console.error("Error registering device token:", error);
        throw error;
    }
}

export async function fetchUserProfile() {
    const passportToken = await getPassportToken();

    if (!passportToken) {
        throw new Error("No passport token found");
    }

    try {
        const response = await fetch(`${ENV.API_BASE_URL}/api/stream/profile`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${passportToken}`,
                'Accept': 'application/json',
            },
        });

        if (!response.ok) {
            if (response.status === 401) {
                authEventEmitter.emitLogout();
                return { name: '', image: '' };
            }
            throw new Error(`Failed to fetch user profile: ${response.statusText}`);
        }

        const data = await response.json();
        console.log("User profile response:", data);
        
        return {
            name: data.name,
            image: data.image || data.profile_photo_url,
        };
    } catch (error) {
        console.error("Error fetching user profile from universe:", error);
        throw error;
    }
}

export async function fetchNotifications(page = 1) {
    const passportToken = await getPassportToken();

    if (!passportToken) {
        throw new Error("No passport token found");
    }

    try {
        const response = await fetch(`${ENV.API_BASE_URL}/api/stream/notifications?page=${page}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${passportToken}`,
                'Accept': 'application/json',
            },
        });

        if (!response.ok) {
            if (response.status === 401) {
                authEventEmitter.emitLogout();
                return { success: false, data: [], meta: {} };
            }
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Failed to fetch notifications: ${response.statusText}`,
                error: errorData
            };
        }

        const data = await response.json();
        
        // Update the native count badge if unread_count is present
        const count = data.data?.unread_count;
        if (Platform.OS !== 'web' && typeof count === 'number') {
            await Notifications.setBadgeCountAsync(count);
        }

        return { success: true, ...data };
    } catch (error: any) {
        console.error("Error fetching notifications from universe:", error);
        return {
            success: false,
            message: error.message || "An unexpected error occurred",
            error
        };
    }
}

export async function createChannel(inscription_id: string, is_guide_finder: boolean) {
    const passportToken = await getPassportToken();

    if (!passportToken) {
        throw new Error("No passport token found");
    }

    try {
        const response = await fetch(`${ENV.API_BASE_URL}/api/stream/create-channel`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${passportToken}`,
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                inscription_id,
                is_guide_finder,
            }),
        });

        if (!response.ok) {
            if (response.status === 401) {
                authEventEmitter.emitLogout();
                return null;
            }
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Failed to create channel: ${response.statusText}`,
                error: errorData
            };
        }

        const data = await response.json();
        console.log("Create channel response:", data);
        return data;
    } catch (error: any) {
        console.error("Error creating channel:", error);
        return {
            success: false,
            message: error.message || "An unexpected error occurred",
            error
        };
    }
}

export async function markNotificationAsRead(notification_id: string) {
    const passportToken = await getPassportToken();

    if (!passportToken) {
        throw new Error("No passport token found");
    }

    try {
        const response = await fetch(`${ENV.API_BASE_URL}/api/stream/notifications/${notification_id}/read`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${passportToken}`,
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                notification_id,
            }),
        });

        if (!response.ok) {
            if (response.status === 401) {
                authEventEmitter.emitLogout();
                return null;
            }
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Failed to mark notification as read: ${response.statusText}`,
                error: errorData
            };
        }

        const data = await response.json();

        // Update the native count badge if unread_count is present
        const count = data.data?.unread_count;
        if (Platform.OS !== 'web' && typeof count === 'number') {
            await Notifications.setBadgeCountAsync(count);
        }

        return data;
    } catch (error: any) {
        console.error("Error marking notification as read:", error);
        return {
            success: false,
            message: error.message || "An unexpected error occurred",
            error
        };
    }
}

export async function deleteNotification(notification_id: string) {
    const passportToken = await getPassportToken();
    if (!passportToken) {
        throw new Error("No passport token found");
    }

    try {
        const response = await fetch(`${ENV.API_BASE_URL}/api/stream/notifications/${notification_id}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${passportToken}`,
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            if (response.status === 401) {
                authEventEmitter.emitLogout();
                return null;
            }
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Failed to delete notification: ${response.statusText}`,
                error: errorData
            };
        }

        const data = await response.json();
        console.log("Delete notification response:", data);
        
        // Update the native count badge if unread_count is present
        const count = data.data?.unread_count;
        if (Platform.OS !== 'web' && typeof count === 'number') {
            await Notifications.setBadgeCountAsync(count);
        }

        return data;
    } catch (error: any) {
        //console.error("Error deleting notification:", error);
        return {
            success: false,
            message: error.message || "An unexpected error occurred",
            error
        };
    }
}

export async function deleteChannel(channel_id: string) {
    const passportToken = await getPassportToken();
    if (!passportToken) {
        throw new Error("No passport token found");
    }

    try {
        const response = await fetch(`${ENV.API_BASE_URL}/api/stream/channels/${channel_id}`, {
            method: 'DELETE',
            headers: {
                'Authorization': `Bearer ${passportToken}`,
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            if (response.status === 401) {
                authEventEmitter.emitLogout();
                return null;
            }
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Failed to delete channel: ${response.statusText}`,
                error: errorData
            };
        }

        const data = await response.json();
        console.log("Delete channel response:", data);
        return data;
    } catch (error: any) {
        console.error("Error deleting channel:", error);
        return {
            success: false,
            message: error.message || "An unexpected error occurred",
            error
        };
    }
}

export async function verifyCode(code: string) {
    const response = await fetch(`${ENV.API_BASE_URL}/api/stream/verify-code`, {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            code,
        }),
    });

    const data = await response.json().catch(() => ({}));
    console.log("Verify code response:", data);
    return data;
}

export async function sendMessageViaTemplate(
    channel_id: string,
    template: { id: string; name?: string | null; language?: string | null }
) {
    const passportToken = await getPassportToken();
    if (!passportToken) {
        throw new Error("No passport token found");
    }

    try {
        const response = await fetch(`${ENV.API_BASE_URL}/api/stream/channels/${channel_id}/message-via-template`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${passportToken}`,
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(
                template.name && template.language
                    ? { name: template.name, language: template.language }
                    : { template_id: template.id }
            ),
        });

        if (!response.ok) {
            if (response.status === 401) {
                authEventEmitter.emitLogout();
                return null;
            }
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Failed to send message via template: ${response.statusText}`,
                error: errorData
            };
        }

        return await response.json();
    } catch (error: any) {
        console.error("Error sending message via template:", error);
        return {
            success: false,
            message: error.message || "An unexpected error occurred",
            error
        };
    }
}

export async function transferChannel(channel_id: string, user_id: number) {
    const passportToken = await getPassportToken();
    if (!passportToken) {
        throw new Error("No passport token found");
    }

    try {
        const response = await fetch(`${ENV.API_BASE_URL}/api/stream/channels/${channel_id}/transfer`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${passportToken}`,
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ user_id }),
        });

        if (!response.ok) {
            if (response.status === 401) {
                authEventEmitter.emitLogout();
                return null;
            }
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Failed to transfer channel: ${response.statusText}`,
                error: errorData
            };
        }

        return await response.json();
    } catch (error: any) {
        console.error("Error transferring channel:", error);
        return {
            success: false,
            message: error.message || "An unexpected error occurred",
            error
        };
    }
}

export async function endChannel(channel_id: string) {
    const passportToken = await getPassportToken();
    if (!passportToken) {
        throw new Error("No passport token found");
    }

    try {
        const response = await fetch(`${ENV.API_BASE_URL}/api/stream/channels/${channel_id}/end`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${passportToken}`,
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            if (response.status === 401) {
                authEventEmitter.emitLogout();
                return null;
            }
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Failed to end channel: ${response.statusText}`,
                error: errorData
            };
        }

        return await response.json();
    } catch (error: any) {
        console.error("Error ending channel:", error);
        return {
            success: false,
            message: error.message || "An unexpected error occurred",
            error
        };
    }
}

export async function fetchChannelUsers(channelId: string, page = 1) {
    const passportToken = await getPassportToken();
    if (!passportToken) throw new Error("No passport token found");

    try {
        const params = new URLSearchParams({ page: String(page) });

        const response = await fetch(
            `${ENV.API_BASE_URL}/api/stream/channels/${channelId}/users?${params}`,
            {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${passportToken}`,
                    'Accept': 'application/json',
                },
            }
        );

        if (!response.ok) {
            if (response.status === 401) {
                authEventEmitter.emitLogout();
                return null;
            }
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Failed to fetch channel users: ${response.statusText}`,
                error: errorData,
            };
        }

        return await response.json();
    } catch (error: any) {
        console.error("Error fetching channel users:", error);
        return {
            success: false,
            message: error.message || "An unexpected error occurred",
            error,
        };
    }
}

export async function fetchChannelTemplates(channel_id: string, page = 1) {
    const passportToken = await getPassportToken();
    if (!passportToken) {
        throw new Error("No passport token found");
    }

    try {
        const response = await fetch(`${ENV.API_BASE_URL}/api/stream/channels/${channel_id}/templates-messages?page=${page}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${passportToken}`,
                'Accept': 'application/json',
            },
        });

        if (!response.ok) {
            if (response.status === 401) {
                authEventEmitter.emitLogout();
                return null;
            }
            const errorData = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorData.message || `Failed to fetch templates: ${response.statusText}`,
                error: errorData
            };
        }

        const data = await response.json();

        return data;
    } catch (error: any) {
        console.error("Error fetching channel templates:", error);
        return {
            success: false,
            message: error.message || "An unexpected error occurred",
            error
        };
    }
}


